import {
  get,
  set,
  push,
  update,
  remove,
  serverTimestamp,
  rolesRef,
  roleRef,
  snapshotToArray,
  snapshotToItem,
} from '@/firebase/database';
import type { Role, Permission } from '@/types';

export interface PermissionCategory {
  name: string;
  description: string;
  permissions: { id: Permission; label: string; description: string }[];
}

export const PERMISSION_GROUPS: PermissionCategory[] = [
  {
    name: 'Estimates',
    description: 'Permissions related to creating, modifying, and exporting estimates',
    permissions: [
      { id: 'Invoice.View', label: 'View Estimates', description: 'Can browse and view estimate details' },
      { id: 'Invoice.Create', label: 'Create Estimates', description: 'Can create new estimates' },
      { id: 'Invoice.Edit', label: 'Edit Estimates', description: 'Can modify draft and open estimates' },
      { id: 'Invoice.Delete', label: 'Delete Estimates', description: 'Can delete estimates' },
      { id: 'Invoice.Finalize', label: 'Finalize / Mark Paid', description: 'Can mark estimates as paid or finalized' },
      { id: 'Invoice.Cancel', label: 'Cancel Estimates', description: 'Can cancel existing estimates' },
      { id: 'Invoice.Print', label: 'Print Estimates', description: 'Can print preview and estimate documents' },
      { id: 'Invoice.Download', label: 'Download PDF', description: 'Can export estimate PDFs' },
    ],
  },
  {
    name: 'Customers',
    description: 'Permissions for customer management and contacts',
    permissions: [
      { id: 'Customer.View', label: 'View Customers', description: 'Can view customer directory and details' },
      { id: 'Customer.Create', label: 'Add Customers', description: 'Can add new customers' },
      { id: 'Customer.Edit', label: 'Edit Customers', description: 'Can update customer info and tax details' },
      { id: 'Customer.Delete', label: 'Delete Customers', description: 'Can delete customers' },
    ],
  },
  {
    name: 'Products & Catalog',
    description: 'Permissions for products, pricing, and catalog items',
    permissions: [
      { id: 'Product.View', label: 'View Products', description: 'Can view product list and pricing' },
      { id: 'Product.Create', label: 'Add Products', description: 'Can add new catalog items' },
      { id: 'Product.Edit', label: 'Edit Products', description: 'Can update pricing, tax, and details' },
      { id: 'Product.Delete', label: 'Delete Products', description: 'Can delete products from catalog' },
    ],
  },
  {
    name: 'Payments',
    description: 'Permissions for logging and viewing payment receipts',
    permissions: [
      { id: 'Payment.View', label: 'View Payments', description: 'Can view payment records and history' },
      { id: 'Payment.Create', label: 'Record Payments', description: 'Can log customer payment receipts' },
    ],
  },
  {
    name: 'Reports & Analytics',
    description: 'Permissions for viewing business reports and metrics',
    permissions: [
      { id: 'Report.View', label: 'View Reports', description: 'Can view sales, GST, customer, and product reports' },
    ],
  },
  {
    name: 'Team & Access Control',
    description: 'Permissions for managing team members and access roles',
    permissions: [
      { id: 'User.Manage', label: 'Manage Team Members', description: 'Can add, edit, activate, and remove users' },
      { id: 'Role.Manage', label: 'Manage Roles', description: 'Can create and configure permissions for roles' },
    ],
  },
  {
    name: 'Settings & Administration',
    description: 'Permissions for company configuration and audit logs',
    permissions: [
      { id: 'Settings.Manage', label: 'Company Settings', description: 'Can update company profile, units, and tax rules' },
      { id: 'Template.Manage', label: 'Template Settings', description: 'Can customize estimate print templates' },
      { id: 'AuditLog.View', label: 'View Audit Logs', description: 'Can inspect system audit trails and user activity' },
    ],
  },
];

export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) =>
  g.permissions.map((p) => p.id),
);

const DEFAULT_ROLES: Array<Omit<Role, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>> = [
  {
    name: 'Admin',
    description: 'Full access to all features and administration settings',
    isSystem: true,
    permissions: ALL_PERMISSIONS,
  },
  {
    name: 'Manager',
    description: 'Access to estimates, customers, products, payments, and reports',
    isSystem: true,
    permissions: [
      'Invoice.Create',
      'Invoice.View',
      'Invoice.Edit',
      'Invoice.Finalize',
      'Invoice.Cancel',
      'Invoice.Print',
      'Invoice.Download',
      'Customer.Create',
      'Customer.View',
      'Customer.Edit',
      'Product.Create',
      'Product.View',
      'Product.Edit',
      'Payment.Create',
      'Payment.View',
      'Report.View',
    ] as Permission[],
  },
  {
    name: 'Staff',
    description: 'Standard billing access for estimate creation, customer entry, and payment collection',
    isSystem: true,
    permissions: [
      'Invoice.Create',
      'Invoice.View',
      'Invoice.Print',
      'Invoice.Download',
      'Customer.Create',
      'Customer.View',
      'Product.View',
      'Payment.Create',
      'Payment.View',
    ] as Permission[],
  },
];

export const getRoleById = async (companyId: string, roleId: string): Promise<Role | null> => {
  if (!companyId || !roleId) return null;

  // 1. Direct path lookup
  try {
    const snap = await get(roleRef(companyId, roleId));
    if (snap.exists()) {
      const item = snapshotToItem<Role>(snap);
      if (item) return item;
    }
  } catch (err) {
    console.warn('Direct role lookup failed:', err);
  }

  // 2. Lookup in all company roles (by id or by name)
  try {
    const all = await getRolesByCompany(companyId);
    const found = all.find(
      (r) => r.id === roleId || r.name.toLowerCase() === roleId.toLowerCase(),
    );
    if (found) return found;
  } catch (err) {
    console.warn('Company roles lookup failed:', err);
  }

  // 3. Fallback to DEFAULT_ROLES
  const def = DEFAULT_ROLES.find((r) => r.name.toLowerCase() === roleId.toLowerCase());
  if (def) {
    return {
      ...def,
      id: roleId,
      companyId,
      createdAt: null as any,
      updatedAt: null as any,
    };
  }

  return null;
};

export const getRolesByCompany = async (companyId: string): Promise<Role[]> => {
  const snap = await get(rolesRef(companyId));
  if (!snap.exists()) {
    // If no roles exist for the company, automatically seed default roles
    await seedDefaultRoles(companyId);
    const retrySnap = await get(rolesRef(companyId));
    return snapshotToArray<Role>(retrySnap);
  }
  return snapshotToArray<Role>(snap);
};

export const createRole = async (
  companyId: string,
  data: Omit<Role, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>,
): Promise<string> => {
  const newRef = push(rolesRef(companyId));
  const id = newRef.key as string;
  await set(newRef, {
    ...data,
    id,
    companyId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
};

export const updateRole = async (
  companyId: string,
  roleId: string,
  data: Partial<Role>,
): Promise<void> => {
  await update(roleRef(companyId, roleId), { ...data, updatedAt: serverTimestamp() });
};

export const deleteRole = async (companyId: string, roleId: string): Promise<void> => {
  await remove(roleRef(companyId, roleId));
};

export const seedDefaultRoles = async (companyId: string): Promise<string> => {
  let adminRoleId = '';
  for (const role of DEFAULT_ROLES) {
    const newRef = push(rolesRef(companyId));
    const id = newRef.key as string;
    await set(newRef, {
      ...role,
      id,
      companyId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    if (role.name === 'Admin') adminRoleId = id;
  }
  return adminRoleId;
};
